import { useState, useRef, useCallback, useEffect } from "react";
import { useGetSignedUploadUrls } from "services/files";
import s3upload from "services/s3upload";
import { Attachment, UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";

const _toUploadAttachments = (
  uploads_: FileUpload[],
): PlainMessage<UploadAttachment>[] => {
  return uploads_.map((u) => {
    return {
      filename: u.filename,
      contentType: u.contentType,
      fileSize: u.size,
      // Will be blank if these are just references to existing attachments.
      s3Key: u.s3Key || "",
      validationKey: u.validationKey || "",
      inlineReference: u.inlineReference || "",
    };
  });
};

export const fromAttachment = (attachment: Attachment): FileUpload => ({
  filename: attachment.filename,
  contentType: attachment.contentType,
  size: attachment.fileSize,
  attachment: attachment,
  progress: 100,
  url: attachment.url,
  // Need to persist this on the server.
  inlineReference: attachment.inlineReference,
});

export const attachmentsToUploads = (
  attachments: Attachment[],
): PlainMessage<UploadAttachment>[] => {
  return _toUploadAttachments(attachments.map(fromAttachment));
};

const mergeUploads = (uploads: FileUpload[], newUploads: FileUpload[]) => {
  const updatesMap = new Map<string, FileUpload>();
  const used = new Set<string>();
  for (const upl of newUploads) {
    updatesMap.set(upl.filename, upl);
  }
  const mergedList: FileUpload[] = [];
  for (const upl of uploads) {
    if (updatesMap.has(upl.filename)) {
      mergedList.push(updatesMap.get(upl.filename)!);
      used.add(upl.filename);
    } else {
      mergedList.push(upl);
    }
  }
  for (const upl of newUploads) {
    if (!used.has(upl.filename)) {
      mergedList.push(upl);
    }
  }
  return mergedList;
};

export interface FileUpload {
  filename: string;
  contentType: string;
  size: number;
  progress: number;
  inlineReference: string | null;
  // Present if it has already been uploaded.
  attachment?: Attachment;
  // True if it is ephemeral.
  file?: File;
  url?: string;
  validationKey?: string;
  s3Key?: string;
}

interface UseUploaderProps {
  initialAttachments?: Attachment[];
  maxUploadBytes?: number;
  maxUploadBytesNonImage?: number;
  sseRef?: string;
}

type Uploader = {
  onUpload: (
    files: FileList | File[] | null,
    inlineReferences?: (string | null)[],
    callback?: (u: PlainMessage<UploadAttachment>[]) => void,
  ) => void;
  fileUploads: FileUpload[];
  fileOnlyUploads: FileUpload[];
  inlineUploads: FileUpload[];
  clearUploads: () => void;
  removeUpload: (
    filename: string,
    callback?: (u: PlainMessage<UploadAttachment>[]) => void,
  ) => void;
  getCompleteAttachments: () => PlainMessage<UploadAttachment>[];
  withAttachments: (attachments: Attachment[]) => void;
  uploadPercentage: number;
  uploadsInProgress: boolean;
  updateFileUpload: (updates: FileUpload[]) => void;
};

type UploadQueueItem = {
  files: FileList | File[];
  inlineReferences?: (string | null)[];
  callback?: (u: PlainMessage<UploadAttachment>[]) => void;
};

export const useUploader = ({
  initialAttachments = [],
  maxUploadBytes = 30000000,
  maxUploadBytesNonImage = undefined,
  sseRef,
}: UseUploaderProps): Uploader => {
  const [fileUploads, setFileUploads] = useState<FileUpload[]>(
    initialAttachments.map(fromAttachment),
  );
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploadsInProgress, setUploadsInProgress] = useState<boolean>(false);
  const uploadQueue = useRef<UploadQueueItem[]>([]);
  const processingQueue = useRef<boolean>(false);
  const usedFilenames = useRef<Set<string>>(new Set());

  const { request: signedUrlRequest } = useGetSignedUploadUrls();

  const withAttachments = (attachments: Attachment[]) => {
    setFileUploads(attachments.map(fromAttachment));
  };

  const updateFileUpload = (updates: FileUpload[]) => {
    setFileUploads((prevUploads) => mergeUploads(prevUploads, updates));
  };

  // Keep the ref in sync with current file uploads
  useEffect(() => {
    usedFilenames.current = new Set(fileUploads.map((u) => u.filename));
  }, [fileUploads]);

  const processUpload = useCallback(
    async (
      files: FileList | File[],
      inlineReferences?: (string | null)[],
      callback?: (u: PlainMessage<UploadAttachment>[]) => void,
    ) => {
      if (inlineReferences && inlineReferences.length !== files.length) {
        console.error("File names must match files");
        return;
      }

      setUploadsInProgress(true);

      // Get current file uploads state
      let currentUploads: FileUpload[] = [];
      setFileUploads((prev) => {
        currentUploads = prev;
        return prev;
      });

      const uploads: FileUpload[] = [];
      let totalSize = currentUploads.reduce((sum, upl) => sum + upl.size, 0);
      // Use the ref to track filenames across all queued uploads
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > maxUploadBytes) {
          alert(`Attachments are limited to ${maxUploadBytes / 1000000}MB`);
          setUploadsInProgress && setUploadsInProgress(false);
          return;
        }
        // Non-images potentially have a different limit.
        if (
          maxUploadBytesNonImage &&
          !file.type.startsWith("image") &&
          file.size > maxUploadBytesNonImage
        ) {
          alert(
            `Non-image attachments are limited to ${
              maxUploadBytesNonImage / 1000000
            }MB`,
          );
          setUploadsInProgress && setUploadsInProgress(false);
          return;
        }
        totalSize += file.size;
        if (totalSize > maxUploadBytes) {
          alert(
            `Total attachment size must not exceed than ${
              maxUploadBytes / 1000000
            }MB`,
          );
          setUploadsInProgress && setUploadsInProgress(false);
          return;
        }
        let filename = file.name;
        if (usedFilenames.current.has(filename)) {
          let j = 1;
          while (usedFilenames.current.has(filename)) {
            filename = `${file.name} (${j})`;
            j++;
          }
        }
        usedFilenames.current.add(filename);
        uploads.push({
          filename: filename,
          contentType: file.type,
          size: file.size,
          file: file,
          progress: 0,
          inlineReference: inlineReferences ? inlineReferences[i] : null,
        });
      }
      const response = await signedUrlRequest({
        // with KMS?
        uploads: uploads.map((u) => {
          return {
            filename: u.filename,
            contentType: u.contentType,
            size: u.size,
          };
        }),
        sseRef: sseRef || "",
      });
      if (!response) {
        // error!
        setUploadsInProgress && setUploadsInProgress(false);
        return;
      }
      updateFileUpload(uploads);
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const signedUrl = response.signedUrls[i];
        upload.url = signedUrl.url;
        upload.validationKey = response.validationKey;
        upload.s3Key = signedUrl.key;
        const progress = (percentage: number) => {
          upload.progress = percentage;
          updateFileUpload(uploads);
          const totalProgress = uploads.reduce(
            (acc, u) => acc + (u.progress / 100) * u.size,
            0,
          );
          const totalSize = uploads.reduce((acc, u) => acc + u.size, 0);
          setUploadPercentage((totalProgress / totalSize) * 100);
        };
        if (signedUrl.filename === upload.filename) {
          await s3upload(
            signedUrl.url,
            upload.file!,
            response.validationKey,
            progress,
            signedUrl.headers,
          );
          upload.progress = 100; // Otherwise is updates to zero at the end.
        }
      }
      updateFileUpload(uploads);
      setUploadsInProgress(false);
      setUploadPercentage(0);
      if (callback) {
        const mergedUploads = mergeUploads(currentUploads, uploads);
        callback(_toUploadAttachments(mergedUploads));
      }
    },
    [
      signedUrlRequest,
      maxUploadBytes,
      maxUploadBytesNonImage,
      sseRef,
      updateFileUpload,
    ],
  );

  const processQueue = useCallback(async () => {
    if (processingQueue.current || uploadQueue.current.length === 0) {
      return;
    }

    processingQueue.current = true;

    while (uploadQueue.current.length > 0) {
      const item = uploadQueue.current.shift();
      if (item) {
        await processUpload(item.files, item.inlineReferences, item.callback);
      }
    }

    processingQueue.current = false;
  }, [processUpload]);

  useEffect(() => {
    processQueue();
  }, [processQueue]);

  const onUpload = useCallback(
    (
      files: FileList | File[] | null,
      inlineReferences?: (string | null)[],
      callback?: (u: PlainMessage<UploadAttachment>[]) => void,
    ) => {
      if (!files) return;

      uploadQueue.current.push({
        files: Array.from(files),
        inlineReferences,
        callback,
      });

      processQueue();
    },
    [processQueue],
  );

  const removeUpload = (
    filename: string,
    callback?: (u: PlainMessage<UploadAttachment>[]) => void,
  ) => {
    const filtered = fileUploads.filter((u) => u.filename !== filename);
    setFileUploads(filtered);
    if (callback) {
      callback(_toUploadAttachments(filtered));
    }
  };

  return {
    onUpload,
    fileUploads,
    fileOnlyUploads: fileUploads.filter((u) => !u.inlineReference),
    inlineUploads: fileUploads.filter((u) => u.inlineReference),
    clearUploads: () => setFileUploads([]),
    removeUpload,
    getCompleteAttachments: () => _toUploadAttachments(fileUploads),
    withAttachments,
    uploadPercentage,
    uploadsInProgress,
    updateFileUpload,
  };
};
